
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "./utils";

interface BudgetSummaryProps {
  baseTotal: number;
  discountRate: number;
  discountAmount: number;
  subtotal: number;
  vatAmount: number;
  totalBudget: number;
  mediaOwnerDiscounts?: Record<string, { discountRate: number; discountAmount: number }>;
}

const BudgetSummary = ({ 
  baseTotal, 
  discountRate, 
  discountAmount, 
  subtotal, 
  vatAmount, 
  totalBudget,
  mediaOwnerDiscounts = {}
}: BudgetSummaryProps) => {
  const { language } = useLanguage();
  const hasMediaOwnerDiscounts = Object.keys(mediaOwnerDiscounts).length > 0;

  return (
    <div className="pt-4 border-t border-border">
      <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {language === 'ar' ? ':المجموع الأساسي' : 'Base Total:'}
        </span>
        <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {formatNumber(baseTotal)} SAR
        </span>
      </div>

      {hasMediaOwnerDiscounts ? (
        // Show individual media owner discounts
        Object.entries(mediaOwnerDiscounts).map(([owner, { discountRate, discountAmount }]) => 
          discountRate > 0 && (
            <div 
              key={owner}
              className={`flex justify-between text-green-600 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
            >
              <span className={language === 'ar' ? 'text-lg' : 'text-base'}>
                {language === 'ar' 
                  ? `:خصم ${owner} (${(discountRate * 100).toFixed(0)}٪)`
                  : `${owner} Discount (${(discountRate * 100).toFixed(0)}%):`}
              </span>
              <span className={language === 'ar' ? 'text-lg' : 'text-base'}>
                -{formatNumber(discountAmount)} SAR
              </span>
            </div>
          )
        )
      ) : (
        // Show total discount if no breakdown
        discountRate > 0 && (
          <div className={`flex justify-between text-green-600 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <span className={language === 'ar' ? 'text-lg' : 'text-base'}>
              {language === 'ar' 
                ? `:خصم الحجم (${(discountRate * 100).toFixed(0)}٪)`
                : `Volume Discount (${(discountRate * 100).toFixed(0)}%):`}
            </span>
            <span className={language === 'ar' ? 'text-lg' : 'text-base'}>
              -{formatNumber(discountAmount)} SAR
            </span>
          </div>
        )
      )}

      <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {language === 'ar' ? ':المجموع الفرعي' : 'Subtotal:'}
        </span>
        <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {formatNumber(subtotal)} SAR
        </span>
      </div>

      <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {language === 'ar' ? ':ضريبة القيمة المضافة (15٪)' : 'VAT (15%):'}
        </span>
        <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
          {formatNumber(vatAmount)} SAR
        </span>
      </div>

      <div className={`flex justify-between mt-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <span className={`${language === 'ar' ? 'text-xl' : 'text-lg'} font-semibold text-foreground`}>
          {language === 'ar' ? ':الميزانية الإجمالية' : 'Total Budget:'}
        </span>
        <span className={`${language === 'ar' ? 'text-xl' : 'text-lg'} font-bold text-primary`}>
          {formatNumber(totalBudget)} SAR
        </span>
      </div>
    </div>
  );
};

export default BudgetSummary;
